import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';

interface DashboardMetrics {
  revenue: {
    total: {
      reservations: number;
      amenities: number;
      pos: number;
    };
    averages: {
      stayValue: number;
      orderValue: number;
      transaction: number;
    };
    counts: {
      reservations: number;
      amenityOrders: number;
      transactions: number;
      totalNights: number;
    };
    trends: {
      daily: Array<{ _id: string; revenue: number; reservations: number }>;
    };
  };
  occupancy: {
    overall: {
      occupancyRate: string;
      totalSites: number;
      occupiedNights: number;
      totalPossibleNights: number;
      averageStayLength: number;
    };
    siteTypes: Array<{
      _id: string;
      reservations: number;
      revenue: number;
      averageRate: number;
    }>;
  };
  guests: {
    acquisition: {
      newGuests: number;
      returningGuests: number;
      totalActiveGuests: number;
    };
    satisfaction: {
      averageRating: number;
      totalReviews: number;
    };
  };
  amenities: {
    topPerformers: Array<{
      _id: string;
      name: string;
      category: string;
      totalOrders: number;
      totalRevenue: number;
      averageOrderValue: number;
    }>;
    categories: Array<{
      _id: string;
      totalOrders: number;
      totalRevenue: number;
    }>;
  };
  sms: {
    volume: {
      total: number;
      delivered: number;
      deliveryRate: string;
    };
    cost: {
      total: string;
      averagePerMessage: string;
    };
    campaigns: Array<{
      _id: string;
      count: number;
      delivered: number;
    }>;
  };
  pos: {
    sales: {
      totalSales: number;
      totalTransactions: number;
      averageTransaction: number;
      totalItems: number;
    };
    paymentMethods: Array<{
      _id: string;
      count: number;
      amount: number;
    }>;
    topItems: Array<{
      _id: string;
      totalQuantity: number;
      totalRevenue: number;
    }>;
  };
  generatedAt: string;
  dateRange: {
    start: string;
    end: string;
  };
}

interface AnalyticsReport {
  _id: string;
  name: string;
  description: string;
  type: string;
  category: string;
  configuration: any;
  scheduling: {
    isScheduled: boolean;
    frequency?: string;
    nextRun?: string;
  };
  usage: {
    runCount: number;
    lastRun?: string;
    avgExecutionTime: number;
  };
  createdAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AnalyticsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null);
  const [reports, setReports] = useState<AnalyticsReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(30, 'day'),
    end: dayjs()
  });
  const [createReportDialog, setCreateReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AnalyticsReport | null>(null);

  useEffect(() => {
    fetchDashboardMetrics();
    fetchReports();
  }, []);

  const fetchDashboardMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/analytics/dashboard', {
        params: {
          startDate: dateRange.start.toISOString(),
          endDate: dateRange.end.toISOString()
        }
      });
      setDashboardMetrics(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await api.get('/analytics/reports');
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: Dayjs | null) => {
    if (value) {
      setDateRange(prev => ({ ...prev, [field]: value }));
    }
  };

  const refreshMetrics = () => {
    fetchDashboardMetrics();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const MetricCard = ({ title, value, subtitle, icon, trend }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    trend?: 'up' | 'down' | 'neutral';
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
            {subtitle && (
              <Typography color="textSecondary" variant="body2">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color="primary.main">
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !dashboardMetrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Analytics Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <DatePicker
            label="Start Date"
            value={dateRange.start}
            onChange={(value) => handleDateRangeChange('start', value)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End Date"
            value={dateRange.end}
            onChange={(value) => handleDateRangeChange('end', value)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshMetrics}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Dashboard Overview" />
          <Tab label="Revenue Analytics" />
          <Tab label="Occupancy Reports" />
          <Tab label="Guest Analytics" />
          <Tab label="Custom Reports" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {dashboardMetrics && (
          <Grid container spacing={3}>
            {/* Key Metrics Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Revenue"
                value={formatCurrency(
                  dashboardMetrics.revenue.total.reservations +
                  dashboardMetrics.revenue.total.amenities +
                  dashboardMetrics.revenue.total.pos
                )}
                subtitle="Last 30 days"
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Occupancy Rate"
                value={`${dashboardMetrics.occupancy.overall.occupancyRate}%`}
                subtitle={`${dashboardMetrics.occupancy.overall.occupiedNights} nights occupied`}
                icon={<AssessmentIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Reservations"
                value={dashboardMetrics.revenue.counts.reservations}
                subtitle={`Avg stay: ${dashboardMetrics.occupancy.overall.averageStayLength.toFixed(1)} nights`}
                icon={<ScheduleIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Guest Satisfaction"
                value={dashboardMetrics.guests.satisfaction.averageRating.toFixed(1)}
                subtitle={`${dashboardMetrics.guests.satisfaction.totalReviews} reviews`}
                icon={<AnalyticsIcon fontSize="large" />}
              />
            </Grid>

            {/* Revenue Trend Chart */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Daily Revenue Trend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dashboardMetrics.revenue.trends.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id" />
                    <YAxis />
                    <ChartTooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Site Type Performance */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Site Type Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardMetrics.occupancy.siteTypes}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ _id, revenue }) => `${_id}: ${formatCurrency(revenue)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revenue"
                    >
                      {dashboardMetrics.occupancy.siteTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip formatter={(value) => formatCurrency(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Top Amenities */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Top Performing Amenities
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardMetrics.amenities.topPerformers.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Bar dataKey="totalRevenue" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Payment Methods */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Payment Methods Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardMetrics.pos.paymentMethods}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ _id, amount }) => `${_id}: ${formatCurrency(amount)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {dashboardMetrics.pos.paymentMethods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip formatter={(value) => formatCurrency(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>Revenue Analytics</Typography>
        {dashboardMetrics && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Reservation Revenue"
                value={formatCurrency(dashboardMetrics.revenue.total.reservations)}
                subtitle={`${dashboardMetrics.revenue.counts.reservations} reservations`}
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Amenity Revenue"
                value={formatCurrency(dashboardMetrics.revenue.total.amenities)}
                subtitle={`${dashboardMetrics.revenue.counts.amenityOrders} orders`}
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="POS Revenue"
                value={formatCurrency(dashboardMetrics.revenue.total.pos)}
                subtitle={`${dashboardMetrics.revenue.counts.transactions} transactions`}
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>Occupancy Reports</Typography>
        {dashboardMetrics && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Overall Occupancy"
                value={`${dashboardMetrics.occupancy.overall.occupancyRate}%`}
                subtitle={`${dashboardMetrics.occupancy.overall.occupiedNights} of ${dashboardMetrics.occupancy.overall.totalPossibleNights} nights`}
                icon={<AssessmentIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Average Stay Length"
                value={`${dashboardMetrics.occupancy.overall.averageStayLength.toFixed(1)} nights`}
                subtitle={`${dashboardMetrics.occupancy.overall.totalSites} total sites`}
                icon={<ScheduleIcon fontSize="large" />}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>Guest Analytics</Typography>
        {dashboardMetrics && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="New Guests"
                value={dashboardMetrics.guests.acquisition.newGuests}
                subtitle="This period"
                icon={<AnalyticsIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Returning Guests"
                value={dashboardMetrics.guests.acquisition.returningGuests}
                subtitle="Repeat customers"
                icon={<AnalyticsIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Average Rating"
                value={dashboardMetrics.guests.satisfaction.averageRating.toFixed(1)}
                subtitle={`${dashboardMetrics.guests.satisfaction.totalReviews} reviews`}
                icon={<AnalyticsIcon fontSize="large" />}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Custom Reports</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateReportDialog(true)}
          >
            Create Report
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {reports.map((report) => (
            <Grid item xs={12} md={6} lg={4} key={report._id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h3">
                      {report.name}
                    </Typography>
                    <Box>
                      <Chip
                        label={report.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  
                  <Typography color="textSecondary" gutterBottom>
                    {report.description}
                  </Typography>
                  
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={report.type} size="small" />
                    {report.scheduling.isScheduled && (
                      <Chip
                        label={`Scheduled (${report.scheduling.frequency})`}
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary">
                    Created by: {report.createdBy.firstName} {report.createdBy.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Run count: {report.usage.runCount}
                  </Typography>
                  {report.usage.lastRun && (
                    <Typography variant="body2" color="textSecondary">
                      Last run: {new Date(report.usage.lastRun).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
                
                <CardActions>
                  <Tooltip title="View Report">
                    <IconButton size="small" onClick={() => setSelectedReport(report)}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Report">
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Report">
                    <IconButton size="small">
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Report">
                    <IconButton size="small">
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Create Report Dialog */}
      <Dialog open={createReportDialog} onClose={() => setCreateReportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Report</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary" gutterBottom>
            Custom report creation coming soon. Use the pre-built analytics above for now.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateReportDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}